import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Crown, Copy, Lock, Users, Building2, FileText, Upload, Trash2, Save, Edit2, Sparkles, LogOut, Eye } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import ContentRequired from "@/components/ContentRequired";
import TeamQuestionsCard from "@/components/TeamQuestionsCard";
import TeamResourcesCard from "@/components/TeamResourcesCard";

export default function MyTeam() {
  const confirm = useConfirm();
  const { user, refreshMe } = useAuth();
  const location = useLocation();
  const previewAsStudent = new URLSearchParams(location.search).get("preview") === "student";
  const previewAs = new URLSearchParams(location.search).get("preview_as");
  const [team, setTeam] = useState(null);
  const [programme, setProgramme] = useState({});
  const [mentor, setMentor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", business_name: "", challenge_statement: "" });

  const realIsAdmin = user?.role === "admin" || user?.role === "faculty";
  const isAdmin = realIsAdmin && !previewAsStudent && !previewAs;
  const isLeader = !!user?.is_team_leader && !previewAsStudent && !previewAs;
  const canEdit = isAdmin || isLeader;

  const load = async () => {
    try {
      const [t, p] = await Promise.all([
        api.get(previewAs ? `/teams/my?as=${previewAs}` : "/teams/my").then((r) => r.data),
        api.get("/programme").then((r) => r.data).catch(() => ({})),
      ]);
      setTeam(t.team);
      setForm({
        name: t.team?.name || "",
        business_name: t.team?.business_name || "",
        challenge_statement: t.team?.challenge_statement || "",
      });
      setProgramme(p || {});
      if (t.team?.assigned_mentor_id && p?.assignments_revealed) {
        try {
          const mm = await api.get("/mentors").then((r) => r.data);
          setMentor((mm || []).find((m) => m.id === t.team.assigned_mentor_id) || null);
        } catch { setMentor(null); }
      } else {
        setMentor(null);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/teams/${team.id}`, {
        name: form.name.trim() || undefined,
        // Only admin can set these two via server-side guard; leaders can only rename.
        business_name: isAdmin ? (form.business_name.trim() || null) : undefined,
        challenge_statement: isAdmin ? (form.challenge_statement.trim() || null) : undefined,
      });
      toast.success("Team saved");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const leave = async () => {
    if (!(await confirm({ title: "Leave this team? You can rejoin with the code.", destructive: false }))) return;
    await api.post("/teams/leave");
    await refreshMe();
    window.location.href = "/team-setup";
  };

  if (!team) {
    return (
      <div className="space-y-6" data-testid="my-team-empty">
        <div>
          <div className="label-eyebrow">My team</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Not in a team yet</h1>
        </div>
        <ContentRequired
          title="Join your team to access the workspace"
          description="Ask an admin to add you to the roster, or use your team's join code below."
          testid="no-team-content-required"
        />
        <Link to="/team-setup" className="inline-block">
          <Button className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="goto-team-setup">Enter join code</Button>
        </Link>
      </div>
    );
  }

  const copyCode = () => {
    navigator.clipboard?.writeText(team.join_code || "");
    toast.success("Join code copied");
  };

  const revealed = !!programme.assignments_revealed;
  const businessShown = revealed || isAdmin;

  return (
    <div className="space-y-8" data-testid="my-team-page">
      {previewAsStudent && realIsAdmin && (
        <div className="border-2 border-[#f4b847] bg-[#FEF9E7] p-3 flex items-center gap-2 text-xs text-[#7A5B17]" data-testid="my-team-preview-banner">
          <Eye size={14} /> <strong>Preview mode</strong> · you&apos;re seeing what students see before Session 2 reveal.
          <a href="/my-team" className="underline hover:no-underline ml-auto">Exit preview</a>
        </div>
      )}
      {previewAs && realIsAdmin && (
        <div className="border-2 border-[#2596be] bg-[#E0F2FF] p-3 flex items-center gap-2 text-xs text-[#1e5077]" data-testid="my-team-impersonate-banner">
          <Eye size={14} /> <strong>Impersonating student</strong> · you&apos;re seeing the exact team this student will unlock at Session 2.
          <a href="/my-team" className="underline hover:no-underline ml-auto">Exit preview</a>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="label-eyebrow flex items-center gap-3 flex-wrap">
            <span>My team</span>
            <span className="font-mono bg-[#0A0A0A] text-white px-2 py-0.5 tracking-widest text-xs">{team.join_code}</span>
            <button onClick={copyCode} className="text-[#525252] hover:text-[#0A0A0A]" data-testid="copy-join-code" title="Copy join code">
              <Copy size={12} />
            </button>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2 text-[#0F1F2E]" data-testid="my-team-name">
            {team.name || `Team ${team.letter || ""}`.trim()}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !editing && (
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="my-team-edit-btn" className="rounded-none">
              <Edit2 size={14} className="mr-1.5" /> Edit
            </Button>
          )}
          {user?.role === "student" && (
            <Button variant="outline" onClick={leave} data-testid="my-team-leave-btn" className="rounded-none border-[#A23232] text-[#A23232] hover:bg-[#FDEDED]">
              <LogOut size={14} className="mr-1.5" /> Leave team
            </Button>
          )}
        </div>
      </div>

      {/* Business + challenge (Session 2 reveal-gated for students) */}
      <div className="border border-[#0A0A0A] bg-white p-6 space-y-5" data-testid="my-team-details-card">
        {editing ? (
          <div className="space-y-4">
            <div>
              <Label className="label-eyebrow">Team name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 rounded-none h-11"
                data-testid="my-team-name-input"
              />
            </div>
            {isAdmin && (
              <>
                <div>
                  <Label className="label-eyebrow">Business</Label>
                  <Input
                    value={form.business_name}
                    onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
                    placeholder="e.g. Mission Café"
                    className="mt-1 rounded-none h-11"
                    data-testid="my-team-business-input"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Challenge statement</Label>
                  <Textarea
                    value={form.challenge_statement}
                    onChange={(e) => setForm((f) => ({ ...f, challenge_statement: e.target.value }))}
                    placeholder="One or two sentences summarising what the team needs to solve for this business."
                    className="mt-1 rounded-none min-h-[120px]"
                    data-testid="my-team-challenge-input"
                  />
                </div>
              </>
            )}
            <div className="flex gap-2">
              <Button onClick={save} className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]" data-testid="my-team-save-btn">
                <Save size={14} className="mr-1.5" /> Save
              </Button>
              <Button variant="outline" onClick={() => { setEditing(false); load(); }} className="rounded-none" data-testid="my-team-cancel-btn">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="label-eyebrow flex items-center gap-2"><Building2 size={12} /> Business</div>
              {businessShown ? (
                <div className="font-display text-2xl font-black tracking-tight mt-1 text-[#0F1F2E]" data-testid="my-team-business">
                  {team.business_name || <span className="text-[#525252] italic font-normal text-base">Not yet assigned</span>}
                </div>
              ) : (
                <div className="mt-2 inline-flex items-center gap-2 border border-[#f4b847] bg-[#FEF9E7] px-3 py-2 text-xs text-[#7A5B17]" data-testid="my-team-business-locked">
                  <Lock size={12} /> Revealed at Session 2
                </div>
              )}
            </div>
            <div>
              <div className="label-eyebrow flex items-center gap-2"><Sparkles size={12} /> Assigned mentor</div>
              {revealed || isAdmin ? (
                mentor ? (
                  <div className="mt-1" data-testid="my-team-mentor">
                    <div className="font-display text-lg font-black tracking-tight text-[#0F1F2E]">{mentor.name}</div>
                    <div className="text-xs text-[#525252]">{mentor.role_title || ""}</div>
                  </div>
                ) : (
                  <div className="mt-1 text-[#525252] italic text-sm">Not yet assigned</div>
                )
              ) : (
                <div className="mt-2 inline-flex items-center gap-2 border border-[#f4b847] bg-[#FEF9E7] px-3 py-2 text-xs text-[#7A5B17]">
                  <Lock size={12} /> Revealed at Session 2
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <div className="label-eyebrow flex items-center gap-2"><FileText size={12} /> Challenge statement</div>
              {businessShown ? (
                team.challenge_statement ? (
                  <p className="mt-2 text-sm text-[#0A0A0A] whitespace-pre-wrap leading-relaxed" data-testid="my-team-challenge">
                    {team.challenge_statement}
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-[#525252] italic">The challenge statement will appear here once your faculty adds it.</p>
                )
              ) : (
                <div className="mt-2 inline-flex items-center gap-2 border border-[#f4b847] bg-[#FEF9E7] px-3 py-2 text-xs text-[#7A5B17]">
                  <Lock size={12} /> Revealed at Session 2
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Members */}
      <div className="border border-[#0A0A0A] bg-white p-6" data-testid="my-team-members-card">
        <div className="label-eyebrow flex items-center gap-2 mb-4"><Users size={12} /> Members · {(team.members || []).length}</div>
        <ul className="divide-y divide-[#E5E7EB]" data-testid="my-team-members-list">
          {(team.members || []).map((m) => (
            <li key={m.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`my-team-member-${m.id}`}>
              <div className="min-w-0 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#25AAAA] text-white flex items-center justify-center text-xs font-semibold uppercase flex-shrink-0">
                  {(m.name || m.email || "U").slice(0, 2)}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-[#0F1F2E] truncate">{m.name || m.email}</div>
                  <div className="text-[11px] uppercase tracking-wider text-[#525252]">{m.email}</div>
                </div>
              </div>
              {m.is_team_leader && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#f4b847] text-[#7A5B17] px-2 py-1 font-bold">
                  <Crown size={10} /> Team Leader
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Team Notes */}
      <TeamNotesCard teamId={team.id} currentUserId={user?.id} isAdmin={isAdmin} />

      {/* Ring-fenced sponsor materials (mentor/admin drop-box) */}
      <TeamResourcesCard teamId={team.id} mentorAssigned={!!team.assigned_mentor_id} />

      {/* Team Questions for the business (post-Session-2) */}
      <TeamQuestionsCard teamId={team.id} />
    </div>
  );
}

function TeamNotesCard({ teamId, currentUserId, isAdmin }) {
  const confirm = useConfirm();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [fileTitle, setFileTitle] = useState("");
  const [file, setFile] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/notes`);
      setNotes(data || []);
    } catch { setNotes([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (teamId) load(); }, [teamId]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/notes`, { title: title.trim(), content: content.trim() || null });
      toast.success("Note added");
      setTitle(""); setContent("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add note");
    } finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", fileTitle.trim() || file.name);
      await api.post(`/teams/${teamId}/notes/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      setFile(null); setFileTitle("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload file");
    } finally { setBusy(false); }
  };

  const del = async (n) => {
    if (!(await confirm({ title: `Delete "${n.title}"?`, destructive: true }))) return;
    try {
      await api.delete(`/teams/${teamId}/notes/${n.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="border border-[#0A0A0A] bg-white p-6 space-y-5" data-testid="team-notes-card">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><FileText size={12} /> Team Notes & Files</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Shared with your mentor</h2>
        <p className="text-xs text-[#525252] mt-2 max-w-2xl">
          Meeting notes, transcripts, PDFs and quick reflections. Visible to everyone on this team, your assigned mentor, and admins.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <form onSubmit={addNote} className="border border-[#E5E7EB] p-4 space-y-3" data-testid="team-note-form">
          <div className="label-eyebrow">Add a text note</div>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Note title"
            data-testid="team-note-title-input"
            className="rounded-none h-11"
          />
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Optional details, meeting notes, decisions…"
            className="rounded-none min-h-[100px]"
            data-testid="team-note-content-input"
          />
          <Button type="submit" disabled={busy} className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]" data-testid="team-note-add-btn">
            {busy ? "Saving…" : "Add note"}
          </Button>
        </form>

        <form onSubmit={uploadFile} className="border border-[#E5E7EB] p-4 space-y-3" data-testid="team-note-upload-form">
          <div className="label-eyebrow">Upload a file</div>
          <Input
            value={fileTitle}
            onChange={(e) => setFileTitle(e.target.value)}
            placeholder="File title (optional)"
            className="rounded-none h-11"
            data-testid="team-note-file-title-input"
          />
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm border border-[#E5E7EB] p-2"
            data-testid="team-note-file-input"
          />
          <p className="text-[11px] text-[#525252]">
            Up to 20 MB. Meeting recordings, transcripts, decks — anything the team + mentor need to reflect on.
          </p>
          <Button type="submit" disabled={busy || !file} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="team-note-upload-btn">
            <Upload size={14} className="mr-1.5" /> {busy ? "Uploading…" : "Upload"}
          </Button>
        </form>
      </div>

      <div>
        {loading ? (
          <div className="text-sm text-[#525252]">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-[#525252] italic">No notes yet. Add the first meeting summary or upload a transcript above.</div>
        ) : (
          <>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search notes, files or author…"
              className="rounded-none h-10 mb-3"
              data-testid="team-notes-search"
            />
            {(() => {
              const q = query.trim().toLowerCase();
              const filtered = q
                ? notes.filter((n) =>
                    (n.title || "").toLowerCase().includes(q) ||
                    (n.content || "").toLowerCase().includes(q) ||
                    (n.author_name || "").toLowerCase().includes(q) ||
                    (n.file_name || "").toLowerCase().includes(q))
                : notes;
              if (filtered.length === 0) {
                return <div className="text-sm text-[#525252] italic">No notes match &quot;{query}&quot;.</div>;
              }
              return (
                <ul className="divide-y divide-[#E5E7EB]" data-testid="team-notes-list">
                  {filtered.map((n) => (
                    <li key={n.id} className="py-3 flex items-start justify-between gap-3" data-testid={`team-note-${n.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider bg-[#0F1F2E] text-white px-1.5 py-0.5 font-bold">
                            {n.kind === "file" ? "File" : "Note"}
                          </span>
                          <div className="font-medium text-[#0F1F2E]">{n.title}</div>
                        </div>
                        {n.content && (
                          <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap mt-2 leading-relaxed">{n.content}</p>
                        )}
                        {n.file_url && (
                          <a href={n.file_url} target="_blank" rel="noreferrer"
                             className="text-sm text-[#2596be] hover:underline mt-2 inline-block"
                             data-testid={`team-note-file-${n.id}`}>
                            {n.file_name || "Download"} ({Math.round((n.file_size || 0) / 1024)} KB)
                          </a>
                        )}
                        <div className="text-[11px] uppercase tracking-wider text-[#525252] mt-2">
                          {n.author_name} · {n.author_role} · {new Date(n.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      {(isAdmin || n.author_id === currentUserId) && (
                        <button onClick={() => del(n)} data-testid={`team-note-delete-${n.id}`}
                                className="text-[#A23232] hover:text-[#7A1F1F]" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
